import {
  type AssignmentForClassroomEntity,
  type AssignedModuleForClassroomEntity,
  AssignedModuleForClassroomSchema
} from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { AssignmentForClassroomSchema } from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { ClassroomDegree } from '@/core/domain/resources/enums/ClassroomDegree'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'

/**
 * Classe représentant un module assigné dans un assignment pour une classe
 * avec toutes les informations enrichies
 */
export class AssignedModuleForClassroom {
  readonly id: number
  readonly assignmentId: number
  readonly classroomId: number
  readonly learningModuleId: number
  readonly learningModuleTitle: string
  readonly learningModuleOrder: number
  readonly learningCourseId: number
  readonly learningCourseOrder: number
  readonly order: number
  readonly visible: boolean
  readonly visibleAt?: string | null

  constructor(assignedModule: AssignedModuleForClassroomEntity) {
    this.id = assignedModule.id
    this.assignmentId = assignedModule.assignmentId
    this.classroomId = assignedModule.classroomId
    this.learningModuleId = assignedModule.learningModuleId
    this.learningModuleTitle = assignedModule.learningModuleTitle
    this.learningModuleOrder = assignedModule.learningModuleOrder
    this.learningCourseId = assignedModule.learningCourseId
    this.learningCourseOrder = assignedModule.learningCourseOrder
    this.order = assignedModule.order
    this.visible = assignedModule.visible
    this.visibleAt = assignedModule.visibleAt
  }

  public copy(patch: Partial<AssignedModuleForClassroom>): AssignedModuleForClassroom {
    return AssignedModuleForClassroom.create({
      id: this.id,
      assignmentId: this.assignmentId,
      classroomId: this.classroomId,
      learningModuleId: this.learningModuleId,
      learningModuleTitle: this.learningModuleTitle,
      learningModuleOrder: this.learningModuleOrder,
      learningCourseId: this.learningCourseId,
      learningCourseOrder: this.learningCourseOrder,
      order: this.order,
      visible: this.visible,
      visibleAt: this.visibleAt,
      ...patch
    })
  }

  public static create(data: unknown): AssignedModuleForClassroom {
    return EntityValidator.validateAndCreate(
      AssignedModuleForClassroomSchema,
      data,
      AssignedModuleForClassroom,
      'AssignedModuleForClassroomSchema'
    )
  }
}

/**
 * Classe représentant un assignment pour une classe avec toutes les informations enrichies
 */
export class AssignmentForClassroom {
  readonly id: number
  readonly schoolPeriodId: number
  readonly degree: ClassroomDegree.Values
  readonly locked: boolean
  readonly assignedModules: AssignedModuleForClassroom[]

  constructor(assignment: AssignmentForClassroomEntity) {
    this.id = assignment.id
    this.schoolPeriodId = assignment.schoolPeriodId
    this.degree = assignment.degree
    this.locked = assignment.locked
    this.assignedModules = assignment.assignedModules.map(
      (module) => new AssignedModuleForClassroom(module)
    )
  }

  /**
   * Vérifie si l'assignment est verrouillé
   */
  public isLocked(): boolean {
    return this.locked
  }

  /**
   * Obtient les modules visibles seulement
   */
  public getVisibleModules(): AssignedModuleForClassroom[] {
    return this.assignedModules.filter((module) => module.visible)
  }

  /**
   * Obtient les modules triés par ordre
   */
  public getModulesByOrder(): AssignedModuleForClassroom[] {
    return [...this.assignedModules].sort((a, b) => {
      if (a == null) return 1
      if (b == null) return -1
      return a.order - b.order
    })
  }

  /**
   * Obtient le nombre de modules visibles
   */
  public getVisibleModulesCount(): number {
    return this.getVisibleModules().length
  }

  /**
   * Obtient le nombre total de modules
   */
  public getTotalModulesCount(): number {
    return this.assignedModules.length
  }

  /**
   * Crée une instance d'AssignmentForClassroom à partir de données brutes
   */
  public static create(data: unknown): AssignmentForClassroom {
    return EntityValidator.validateAndCreate(
      AssignmentForClassroomSchema,
      data,
      AssignmentForClassroom,
      'AssignmentForClassroom'
    )
  }
}
